import { zodResolver } from "@hookform/resolvers/zod"
import { CheckboxGroup } from "react-aria-components"
import { Controller, useForm, useWatch } from "react-hook-form"
import z from "zod"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  EmailInput,
  FirstNameInput,
  Input,
  LastNameInput,
  PhoneInput,
} from "@/components/ui/input"
import { getCsrfToken } from "@/lib/csrf"

import type { RoleType } from "../../../types"

interface NewSchoolContactProp {
  accountId: number
  onSuccess: (id: string) => void
  onCancel: () => void
}

type NewContactFormValues = z.infer<typeof NewSchoolContactSchema>

const ROLE_OPTIONS: { value: RoleType; label: string }[] = [
  { value: "principal", label: "Principal" },
  { value: "wellness_coordinator", label: "Wellness Coordinator" },
  { value: "supervising_teacher", label: "Supervising Teacher" },
  { value: "administrative", label: "Administrative" },
  { value: "after_hours", label: "After-hours Contact" },
  { value: "other", label: "Other" },
]

const NewSchoolContactSchema = z
  .object({
    first_name: z
      .string({ error: "Please enter a first name" })
      .nonempty("Please enter a first name"),
    last_name: z
      .string({ error: "Please enter a last name" })
      .nonempty("Please enter a last name"),
    email: z.email("Please enter a valid email address"),
    telephone_number: z
      .string({ error: "Please enter a phone number" })
      .regex(/^\+?[0-9\s\-().]{6,20}$/, "Please enter a valid phone number"),
    roles: z
      .array(z.enum(ROLE_OPTIONS.map((role) => role.value)))
      .min(1, "Please choose at least one role"),
    other_detail: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.roles.includes("other") && !data.other_detail?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please tell us what the role is",
        path: ["other_detail"],
      })
    }
  })

const NewSchoolContact: React.FC<NewSchoolContactProp> = ({
  accountId,
  onSuccess,
  onCancel,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<NewContactFormValues>({
    resolver: zodResolver(NewSchoolContactSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      telephone_number: "",
      roles: [],
      other_detail: "",
    },
  })

  const roles = useWatch({ control, name: "roles" })

  const buildRolesAttributes = (
    data: NewContactFormValues,
  ): Record<string, { role_type: string; detail?: string }> => {
    const attrs: Record<string, { role_type: string; detail?: string }> = {}
    data.roles.forEach((role, i) => {
      const entry: { role_type: string; detail?: string } = { role_type: role }
      if (role === "other" && data.other_detail) {
        entry.detail = data.other_detail
      }
      attrs[String(i)] = entry
    })
    return attrs
  }

  const onSubmit = handleSubmit(async (data) => {
    const body: Record<string, unknown> = {
      ...data,
      account_id: accountId,
      contact_roles_attributes: buildRolesAttributes(data),
    }

    const res = await fetch("/forms/school_contacts", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": getCsrfToken(),
      },
      body: JSON.stringify(body),
    })

    if (res.ok) {
      const result = (await res.json()) as { id: number }
      onSuccess(String(result.id))
    }
  })

  return (
    <form onSubmit={onSubmit} noValidate>
      <div className="flex flex-col gap-4 px-4 pt-5 sm:px-6 sm:pt-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Controller
            control={control}
            name="first_name"
            render={({ field: { value, onChange, onBlur } }) => (
              <FirstNameInput
                label="First Name"
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={errors.first_name}
              />
            )}
          />
          <Controller
            control={control}
            name="last_name"
            render={({ field: { value, onChange, onBlur } }) => (
              <LastNameInput
                label="Last Name"
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={errors.last_name}
              />
            )}
          />
        </div>
        <Controller
          control={control}
          name="email"
          render={({ field: { value, onChange, onBlur } }) => (
            <EmailInput
              label="Email Address"
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={errors.email}
            />
          )}
        />
        <Controller
          control={control}
          name="telephone_number"
          render={({ field: { value, onChange, onBlur } }) => (
            <PhoneInput
              label="Phone number"
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              isRequired
              error={errors.telephone_number}
            />
          )}
        />

        <div className="flex flex-col gap-2">
          <span className="text-secondary text-sm font-medium">Roles</span>
          <Controller
            control={control}
            name="roles"
            render={({ field: { value, onChange } }) => (
              <CheckboxGroup value={value} onChange={onChange}>
                {ROLE_OPTIONS.map((opt) => (
                  <Checkbox
                    key={opt.value}
                    label={opt.label}
                    value={opt.value}
                    size="sm"
                  />
                ))}
              </CheckboxGroup>
            )}
          />
          {errors.roles && (
            <span className="text-error-primary text-sm">
              {errors.roles.message}
            </span>
          )}
          {roles.includes("other") && (
            <Controller
              control={control}
              name="other_detail"
              render={({ field: { value, onChange, onBlur } }) => (
                <Input
                  label="Please specify"
                  placeholder="e.g. IT coordinator"
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={errors.other_detail}
                />
              )}
            />
          )}
        </div>
      </div>
      <div className="z-10 flex flex-1 flex-col-reverse gap-3 p-4 pt-6 *:grow sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
        <Button type="button" color="secondary" size="lg" onPress={onCancel}>
          Cancel
        </Button>
        <Button type="submit" color="primary" size="lg">
          Save
        </Button>
      </div>
    </form>
  )
}

export default NewSchoolContact
